"use client";

import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Container,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import bannerImg from "../assets/bannerImg.png";
import callersImg from "../assets/callersImg.png";
import chooseImg from "../assets/chooseImg.png";
import faqImg from "../assets/faqImg.jpg";
import subscribeImg from "../assets/subscribeImg.png";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

// Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import Layout from "../components/Layout";
import { Helmet } from "react-helmet-async";

/* ---------- Types ---------- */
interface Banner {
  imagen: string;
  link: string;
}

interface GridItem {
  imagen: string;
  link: string;
}

interface FAQItem {
  pregunta: string;
  respuesta: string;
}

interface HelpItem {
  titulo: string;
  mensaje: string;
}

interface AlertMessage {
  severity: "success" | "error" | "info" | "warning";
  text: string;
}

const sampleBanners: Banner[] = [
  { imagen: "banner1.jpg", link: "#" },
  { imagen: "banner2.jpg", link: "#" },
  { imagen: "banner3.jpg", link: "#" },
];

const sampleGrid: GridItem[] = [
  { imagen: subscribeImg, link: "#" },
  { imagen: chooseImg, link: "#" },
  { imagen: faqImg, link: "#" },
  { imagen: callersImg, link: "#" },
];

const sampleFAQ: FAQItem[] = [
  {
    pregunta: "What is Flow Tones?",
    respuesta: "A sample ringtone/tones service.",
  },
  {
    pregunta: "How to sign up?",
    respuesta: "Use the signup modal and follow steps.",
  },
];

const sampleHelp: HelpItem[] = [
  { titulo: "How to use", mensaje: "Search and pick your favourite tone." },
];

export default function Login(): JSX.Element {
  const [openFAQ, setOpenFAQ] = useState(false);
  const [openHelp, setOpenHelp] = useState(false);
  const [openTyc, setOpenTyc] = useState(false);

  const [alerts, setAlerts] = useState<AlertMessage[]>([]);

  const swiperRef = useRef<any>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const href = target.getAttribute("href");
      if (href && href.startsWith("#")) {
        const el = document.querySelector(href);
        if (el) {
          e.preventDefault();
          el.scrollIntoView({ behavior: "smooth" });
        }
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return (
    <Layout>
      <Helmet>
        <title>Flow Tones</title>
        <meta name="description" content="Best tones, music, and RBT services." />
        <meta name="keywords" content="rbt, tones, music, crbt" />
      </Helmet>
      <Box sx={{ pt: 10 }}>
        <Container>
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            navigation={{
              prevEl: ".custom-prev",
              nextEl: ".custom-next",
            }}
            pagination={{
              el: ".swiper-pagination",
              clickable: true,
              dynamicBullets: true,
            }}
            autoplay={{ delay: 6000 }}
            loop
            onSwiper={(sw: any) => (swiperRef.current = sw)}
            className="custom-swiper"
          >
            {sampleBanners.map((b, i) => (
              <SwiperSlide key={i}>
                <a href={b.link}>
                  <img
                    src={bannerImg}
                    alt={`banner-${i}`}
                    style={{
                      width: "100%",
                      height: 420,
                      objectFit: "cover",
                    }}
                  />
                </a>
              </SwiperSlide>
            ))}
            <div className="custom-prev">
              <PlayArrowIcon
                sx={{ fontSize: 28, transform: "rotate(180deg)" }}
              />
            </div>

            <div className="custom-next">
              <PlayArrowIcon sx={{ fontSize: 28 }} />
            </div>

            <div className="swiper-pagination"></div>
          </Swiper>

          {/* Grid Banners */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 4 }}>
            {sampleGrid.map((g, i) => (
              <Box
                key={i}
                sx={{
                  flex: "1 1 48%",
                  boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
                  overflow: "hidden",
                  transition: "0.3s",
                  "&:hover": {
                    boxShadow: "0px 4px 16px rgba(0,0,0,0.25)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <a href={g.link}>
                  <img
                    src={g.imagen}
                    alt={`grid-${i}`}
                    style={{
                      width: "100%",
                      height: 180,
                      objectFit: "cover",
                      // borderRadius: 6,
                    }}
                  />
                </a>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      <Box sx={{ position: "fixed", right: 16, top: 80, zIndex: 1400 }}>
        {alerts.map((a, i) => (
          <Alert
            key={i}
            severity={a.severity}
            onClose={() => setAlerts((curr) => curr.filter((_, x) => x !== i))}
            sx={{ mb: 1 }}
          >
            {a.text}
          </Alert>
        ))}
      </Box>

      <Dialog
        open={openFAQ}
        onClose={() => setOpenFAQ(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Frequently Asked Questions</DialogTitle>
        <DialogContent dividers>
          {sampleFAQ.map((q, i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Typography variant="subtitle1">{q.pregunta}</Typography>
              <Typography variant="body2">{q.respuesta}</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFAQ(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* HELP Modal */}
      <Dialog
        open={openHelp}
        onClose={() => setOpenHelp(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Help</DialogTitle>
        <DialogContent dividers>
          {sampleHelp.map((h, i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Typography variant="subtitle1">{h.titulo}</Typography>
              <Typography variant="body2">{h.mensaje}</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHelp(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openTyc}
        onClose={() => setOpenTyc(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Terms & Conditions</DialogTitle>
        <DialogContent dividers>
          <Typography paragraph>
            About Us - Flow - We were the first to connect the Caribbean...
          </Typography>
          <Typography paragraph>Telemune - Founded in 2004...</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTyc(false)}>Ok</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
